// src/components/Login.js
import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import useTeamScores from '../hooks/useTeamScores';

export default function Login() {
    const { signInWithGoogle } = useAuth();
    const { t, toggleLanguage, language } = useLanguage();
    const { teamScores, loading } = useTeamScores();
    const [timeLeft, setTimeLeft] = useState('');

    // Countdown timer to midnight
    useEffect(() => {
        const updateCountdown = () => {
            const now = new Date();
            const midnight = new Date();
            midnight.setHours(24, 0, 0, 0); // Next midnight

            const timeDiff = midnight.getTime() - now.getTime();

            if (timeDiff <= 0) {
                setTimeLeft('00:00:00');
                return;
            }

            const hours = Math.floor(timeDiff / (1000 * 60 * 60));
            const minutes = Math.floor((timeDiff % (1000 * 60 * 60)) / (1000 * 60));
            const seconds = Math.floor((timeDiff % (1000 * 60)) / 1000);

            setTimeLeft(
                `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`
            );
        };

        updateCountdown(); // Initial call
        const interval = setInterval(updateCountdown, 1000);

        return () => clearInterval(interval);
    }, []);

    return (
        <div className="min-h-screen bg-gradient-to-br from-gray-900 to-gray-800 flex items-center justify-center p-4">
            <div className="bg-white p-8 rounded-lg shadow-2xl text-center max-w-md w-full">
                {/* Logo */}
                <div className="mb-6">
                    <img
                        src="/logo512.png"
                        alt="Game Logo"
                        className="w-20 h-20 mx-auto rounded-lg shadow-md"
                    />
                </div>

                <h1 className="text-4xl font-bold mb-6 text-gray-800">{t('title')}</h1>

                {/* Countdown to match end with background image */}
                <div
                    className="mb-4 relative rounded-lg p-4 text-white overflow-hidden"
                    style={{
                        backgroundImage: 'url(/crno-beli-vs-crveno-beli.jpg)',
                        backgroundSize: 'cover',
                        backgroundPosition: 'center',
                        backgroundRepeat: 'no-repeat',
                        minHeight: '120px'
                    }}
                >
                    {/* Overlay za bolje čitanje teksta */}
                    <div className="absolute inset-0 bg-gradient-to-r from-red-600/80 to-orange-600/80 rounded-lg"></div>

                    {/* Sadržaj countdown-a */}
                    <div className="relative z-10">
                        <div className="flex items-center justify-center gap-2 mb-2">
                            <span className="text-lg">⏰</span>
                            <span className="text-sm font-semibold drop-shadow-lg">{t('matchEndsIn')}</span>
                        </div>
                        <div className="text-3xl font-bold font-mono tracking-wider drop-shadow-lg">
                            {timeLeft}
                        </div>
                        <p className="text-sm mt-2 text-white/90 drop-shadow">{t('matchResetMidnight')}</p>
                    </div>
                </div>

                {/* Team scores display */}
                <div className="mb-6 bg-gray-100 p-4 rounded-lg">
                    <h2 className="text-lg font-semibold text-gray-800 mb-2">{t('todayScore')}</h2>
                    <div className="flex justify-center items-center gap-6">
                        <div className="flex flex-col items-center">
                            <div className="flex items-center gap-2">
                                <span className="text-xl">⚫⚪</span>
                                <span className="text-3xl text-gray-800 font-semibold">{teamScores.blackWhite || 0}</span>
                            </div>
                            <span className="text-lg font-semibold text-gray-600">{t('blackWhiteTeam')}</span>
                        </div>
                        <div className="text-gray-600">vs</div>
                        <div className="flex flex-col items-center">
                            <div className="flex items-center gap-2">
                                <span className="text-xl">🔴⚪</span>
                                <span className="text-3xl text-gray-800 font-semibold">{teamScores.redWhite || 0}</span>
                            </div>
                            <span className="text-lg font-semibold text-gray-600">{t('redWhiteTeam')}</span>
                        </div>
                    </div>

                    <p className="mt-3 text-gray-700 font-medium">
                        {t('registerTeamHelp')}
                    </p>
                </div>

                <button
                    onClick={signInWithGoogle}
                    className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-3 mx-auto"
                >
                    <svg className="w-6 h-6" viewBox="0 0 24 24">
                        <path fill="currentColor" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                        <path fill="currentColor" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                        <path fill="currentColor" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                        <path fill="currentColor" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                    </svg>
                    {t('login')}
                </button>

                {/* Divider */}
                <div className="flex items-center my-4">
                    <div className="flex-grow border-t border-gray-300"></div>
                    <span className="px-3 text-sm text-gray-500">
                        {t('orPlayAnonymously')}
                    </span>
                    <div className="flex-grow border-t border-gray-300"></div>
                </div>

                {/* Android App Link */}
                <a
                    href="https://play.google.com/store/apps/details?id=com.vecitiderbi.veciti_derbi"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-full inline-flex items-center justify-center gap-3 px-6 py-3 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors"
                    title={t('playAnonymously')}
                >
                    <span className="text-xl">📱</span>
                    <div className="text-left">
                        <div className="font-semibold">{t('downloadAndroidApp')}</div>
                        <div className="text-sm opacity-90">{t('playAnonymously')}</div>
                    </div>
                </a>

                {/* Language Toggle */}
                <div className="mt-6">
                    <button
                        onClick={toggleLanguage}
                        className="text-sm text-gray-600 hover:text-gray-800"
                    >
                        {t('language')}: {language.toUpperCase()}
                    </button>
                </div>
            </div>
        </div>
    );
}